import React from "react";
import { Box, BoxProps, Button, ButtonProps, Link, LinkProps } from "@chakra-ui/react";
import { landing } from "../landingTokens";
import { useLandingCopy } from "../LandingLanguageContext";
import { useLandingTheme } from "../LandingThemeContext";
import {
  APP_STORES,
  AppStore,
  AppStorePlacement,
  CONTINUE_IN_BROWSER_CLASS_NAME,
  continueInBrowser,
  trackAppStoreClick,
} from "../handoff/appStoreRoute";
import { useAppStoreUrl, useIsHydrated } from "../handoff/useAppUrl";

/*
 * The store calls to action for phone visitors from a Meta ad (see handoff/appStoreRoute.ts). Everything here
 * renders for every visitor, identically at build time and in the browser; the classes below are what
 * marketingContent.css keys on to show them, from <html data-app-store-route>:
 *   web-app-cta             a web call to action, hidden for routed visitors (put it on the element it replaces)
 *   app-store-cta--<store>  a store link, shown only to visitors routed to that store
 *   app-store-route-only    shown only to routed visitors
 * Never give these elements a Chakra `display` prop: the stylesheet owns their display.
 *
 * Every static page carries its markup twice over (both landing designs) under an HTML size budget
 * (checkMarketingSeo.mjs), so only the hero's store link is in the build-time rendering, where it must work
 * before any JavaScript runs. The other placements (closing call to action, footer, the desktop nav a phone
 * only shows sideways) pass `onlyAfterHydration`: they render nothing at build time and in React's first render
 * (so hydration still matches) and appear right after; until then a routed visitor's web link there is already
 * hidden by the stylesheet. The phone menu renders only when opened, so it needs neither.
 */
export const WEB_APP_CTA_CLASS_NAME = "web-app-cta";
const APP_STORE_ROUTE_ONLY_CLASS_NAME = "app-store-route-only";

function appStoreCtaClassName(store: AppStore): string {
  return `app-store-cta app-store-cta--${store}`;
}

type StoreLinkLabel = "store_name" | "get_the_app";

function useStoreLinkText(store: AppStore, label: StoreLinkLabel): string {
  const callsToActionCopy = useLandingCopy().callsToAction;
  if (label === "get_the_app") return callsToActionCopy.getTheAppButton;
  return store === "ios" ? callsToActionCopy.appStoreBadgeAlt : callsToActionCopy.googlePlayBadgeAlt;
}

type AppStoreButtonProps = Omit<ButtonProps, "as" | "href" | "onClick" | "children" | "display" | "className"> & {
  placement: AppStorePlacement;
  // "store_name" says where the link goes ("Download on the App Store"); "get_the_app" is the short label.
  label: StoreLinkLabel;
};

function AppStoreButton({ store, placement, label, ...buttonProps }: AppStoreButtonProps & { store: AppStore }) {
  const href = useAppStoreUrl(store);
  const text = useStoreLinkText(store, label);
  return (
    <Button
      as="a"
      href={href}
      className={appStoreCtaClassName(store)}
      onClick={() => trackAppStoreClick(store, placement)}
      {...buttonProps}
    >
      {text}
    </Button>
  );
}

/** One button per store; only the routed visitor's store is shown. Same tab: phones hand the link to the store app. */
export function AppStoreButtons({ onlyAfterHydration = false, ...props }: AppStoreButtonProps & DeferredRenderingProps) {
  const isHydrated = useIsHydrated();
  if (onlyAfterHydration && !isHydrated) return null;
  return (
    <>
      {APP_STORES.map((store) => (
        <AppStoreButton key={store} store={store} {...props} />
      ))}
    </>
  );
}

type AppStoreTextLinkProps = Omit<LinkProps, "href" | "onClick" | "children" | "display" | "className" | "isExternal"> & {
  placement: AppStorePlacement;
};

type DeferredRenderingProps = {
  // Leave the links out of the build-time rendering (see the comment at the top of this file).
  onlyAfterHydration?: boolean;
};

function AppStoreTextLink({ store, placement, ...linkProps }: AppStoreTextLinkProps & { store: AppStore }) {
  const href = useAppStoreUrl(store);
  const text = useStoreLinkText(store, "get_the_app");
  return (
    <Link
      href={href}
      className={appStoreCtaClassName(store)}
      onClick={() => trackAppStoreClick(store, placement)}
      {...linkProps}
    >
      {text}
    </Link>
  );
}

/** The text-link form ("Get the app"), for link rows such as the footer. */
export function AppStoreTextLinks({ onlyAfterHydration = false, ...props }: AppStoreTextLinkProps & DeferredRenderingProps) {
  const isHydrated = useIsHydrated();
  if (onlyAfterHydration && !isHydrated) return null;
  return (
    <>
      {APP_STORES.map((store) => (
        <AppStoreTextLink key={store} store={store} {...props} />
      ))}
    </>
  );
}

/**
 * The store button that replaces a primary web call to action (the hero upload, the closing button), with the
 * "Continue in browser" way back underneath. Hidden unless the visitor is routed.
 */
export function AppStoreCallToAction({
  placement,
  label,
  buttonProps,
  continueInBrowserColor,
  onlyAfterHydration = false,
  ...boxProps
}: Omit<BoxProps, "display" | "className"> &
  DeferredRenderingProps & {
    placement: AppStorePlacement;
    label: StoreLinkLabel;
    buttonProps: Omit<AppStoreButtonProps, "placement" | "label">;
    continueInBrowserColor: string;
  }) {
  const callsToActionCopy = useLandingCopy().callsToAction;
  const isHydrated = useIsHydrated();
  if (onlyAfterHydration && !isHydrated) return null;
  return (
    <Box className={APP_STORE_ROUTE_ONLY_CLASS_NAME} {...boxProps}>
      <AppStoreButtons placement={placement} label={label} {...buttonProps} />
      {/* Plain elements styled by marketingContent.css: no per-theme generated CSS in every page. The click also
          works before hydration: marketing.html's route script listens for this class (appStoreRoute.ts). */}
      <div>
        <button
          type="button"
          className={CONTINUE_IN_BROWSER_CLASS_NAME}
          style={{ color: continueInBrowserColor }}
          onClick={() => continueInBrowser(placement)}
        >
          {callsToActionCopy.continueInBrowserButton}
        </button>
      </div>
    </Box>
  );
}

/** The hero's store call to action, styled like the upload button it stands in for. */
export function HeroAppStoreCallToAction({
  onDark = false,
  ...boxProps
}: Omit<BoxProps, "display" | "className"> & { onDark?: boolean }) {
  const theme = useLandingTheme();
  return (
    <AppStoreCallToAction
      placement="hero"
      label="store_name"
      w="100%"
      continueInBrowserColor={onDark ? "rgba(255, 255, 255, 0.85)" : theme.color.text}
      buttonProps={{
        w: "100%",
        whiteSpace: "normal",
        minH: "64px",
        h: "auto",
        py: "14px",
        px: "26px",
        bg: theme.color.accent,
        color: theme.button.primaryColor,
        border: theme.button.border,
        fontFamily: theme.font.body,
        fontSize: "20px",
        fontWeight: 600,
        borderRadius: theme.shape.buttonRadius,
        transition: landing.buttonHoverTransition,
        _hover: { bg: theme.color.accentHover, transform: theme.button.hoverTransform, textDecoration: "none" },
        _focusVisible: { boxShadow: `0 0 0 3px ${theme.color.highlight}` },
      }}
      {...boxProps}
    />
  );
}
